import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { environment } from '@pn-plus/pnp-environment';
import { AuthService, AuthErrorService, PnpLoginService, PnpUserService, ILoginTwoFactorResendPayload, IRegistrationResponse, ILoginTwoFactorPayload, IUserPhoneNumberConfirmedPayload, AuthErrors, IUserResponse, IUserPasswordResetResponse, IUserPasswordResetVerificationPayload, IUserPasswordResetResendPayload } from '@pn-plus/pnp-services';
import { ITokenInfo2FA, ITokenInfo } from 'libs/pnp-services/src/lib/api/_models/pnp-token-info.model';
import { firstValueFrom, Observable, catchError, of, map, EMPTY } from 'rxjs';
import { IdentityVerificationComponent } from '../../core/components/identity-verification/identity-verification.component';
import { LoginService } from '../../core/services/login.service';

@Component({
    selector: 'pnl-verify-recovery-phone',
    templateUrl: './verify-recovery-phone.component.html',
    styleUrls: ['./verify-recovery-phone.component.scss'],
})
export class VerifyRecoveryPhoneComponent implements OnInit {
    @ViewChild('identityVerification') identityVerificationRef !: IdentityVerificationComponent;
    
    public isLoading = false;
    public activeError: any;

    public isResetPassword = false;
    public resetPasswordPayload: IUserPasswordResetResponse;
    public resetPasswordPhoneNumber = '';

    get phoneNumber(): string {
        const authModel = this.authService.getAuthModel();
        const number = authModel?.userResponse?.phoneNumber || '****';
        return number.length > 4 ? number.substr(number.length - 4, 4) : number;
    }

    constructor(
        public authService: AuthService,
        public loginService: LoginService,
        private authErrorService: AuthErrorService,
        private pnpLoginService: PnpLoginService,
        private pnpUserService: PnpUserService,
    ) {
        this.resetPasswordPayload = this.authService.getFormDataRecoveryResponse();
        if (this.resetPasswordPayload !== null) {
            this.isResetPassword = true;
            this.resetPasswordPhoneNumber = this.resetPasswordPayload.userPhoneNumberLastFour;
        }
    }

    getTwoFactorToken(): ITokenInfo2FA | undefined {
        const authModel = this.authService.getAuthModel();
        return authModel?.loginResponse?.twoFactorToken;
    }

    async ngOnInit() {
        this.setBusy(true);
        const authModel = this.authService.getAuthModel();
        if(!authModel.registrationResponse?.hasPhoneNumberConfirmed && !authModel.phoneNumberResponse) {
            const response = await firstValueFrom(this.resendConfirmationCode());
            if(this.activeError === AuthErrors.PhoneTaken || this.activeError === AuthErrors.UserHasNoCellPhone) {
                if(this.activeError === AuthErrors.UserHasNoCellPhone){
                    authModel.error = AuthErrors.Success;
                } else  {
                    authModel.error = this.activeError;
                }
                setTimeout(() => {
                    this.loginService.navigate('/set-phone');
                });
            } else if(!authModel.phoneNumberResponse) {
                authModel.phoneNumberResponse = <any>response;
            }
            this.authService.updateAuthModel(authModel);
        }
        this.setBusy(false);
    }

    resendConfirmationCode() {
        return this.pnpUserService.putUserPhoneNumberResend()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response) => {
                    return response;
                }),
            );
    } 

    async onCodeEntered(code: string) {
        this.setBusy(true);
        this.activeError = AuthErrors.Success;

        await firstValueFrom(this.putUserPhoneNumberConfirmed(code));
        await firstValueFrom(this.putRegistrationHasPhoneNumberConfirmed());
        await firstValueFrom(this.getUserRegistration());
        await firstValueFrom(this.getUser());

        if(!this.activeError) {
            this.loginService.navigateRegistration(this.authService.getAuthModel());
        }

        this.identityVerificationRef.codeInput.reset();
        this.setBusy(false);
    }

    getUserRegistration(): Observable<IRegistrationResponse | null> {
        if(this.activeError) {
            return of(null);
        }
        return this.pnpUserService.getUserRegistration()
            .pipe(
                catchError((errorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response: IRegistrationResponse | null) => {
                    if(response) {
                        const authModel = this.authService.getAuthModel();
                        authModel.registrationResponse = response;
                        this.authService.updateAuthModel(authModel);
                    }
                    return response;
                }),
            );
    }

    getUser(): Observable<IUserResponse | null> {
        if(this.activeError) {
            return of(null);
        }
        
        return this.pnpUserService.getUser()
            .pipe(
                catchError((errorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response: IUserResponse | null) => {
                    if(response) {
                        const authModel = this.authService.getAuthModel();
                        authModel.userResponse = response;
                        this.authService.updateAuthModel(authModel);
                    }
                    return response;
                }),
            );
    }
    
    putRegistrationHasPhoneNumberConfirmed(): Observable<any> {
        if(this.activeError) {
            return of(null);
        }
        return this.pnpUserService.putRegistrationHasPhoneNumberConfirmed()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response) => {
                    const authModel = this.authService.getAuthModel();
                    if(authModel.registrationResponse) {
                        authModel.registrationResponse.hasPhoneNumberConfirmed = true;
                    }
                    this.authService.updateAuthModel(authModel);
                    return response;
                }),
            );
    }

    putUserPhoneNumberConfirmed(code: string): Observable<ITokenInfo> {
        // 1. Phone confirmation
        const authModel = this.authService.getAuthModel();

        const payload: IUserPhoneNumberConfirmedPayload = {
            phoneNumberChangeToken: <any>authModel.phoneNumberResponse?.jwt,
            code: code,
        };

        return this.pnpUserService.putUserPhoneNumberConfirmed(payload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response: any) => {
                    const authModel = this.authService.getAuthModel();
                    if(authModel.userResponse) {
                        authModel.userResponse.phoneNumberConfirmed = true;
                    }
                    this.authService.updateAuthModel(authModel);
                    return response;
                }),
            );
    }
    
    setBusy(flag: boolean): void {
        this.isLoading = flag;
    }

    async onResendClicked($event: any) {
        const authModel = this.authService.getAuthModel();
        const resendToken = await firstValueFrom(this.resendCode());
        if(resendToken?.jwt){
            authModel.phoneNumberResponse = resendToken;
        }
        this.authService.updateAuthModel(authModel);
    }

    resendCode() {

        return this.pnpUserService.putUserPhoneNumberResend()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response: any) => {
                    return response;
                }),
            );
    }

    onErrorAction($event: { error: number, $event: any}) {
        this.loginService.navigate('/');
    }

    onChangeMobileNumberClicked($event: any) {
        this.loginService.navigate('/set-phone');
    }

    // Reset Password section

    async onIdentityVerificationEntered(code: string) {
        this.activeError = null;

        this.setBusy(true);
        const response = await firstValueFrom(this.verifyResetPasswordCode(code));
        this.setBusy(false);

        if(this.activeError) {
            this.identityVerificationRef.reset();
            if(this.activeError === AuthErrors.TokenExpired || this.activeError === AuthErrors.TokenNotValid){
                this.loginService.navigate('/login');
            }
            return;
        }

        const isInvitationProcess = this.authService.getIsInvitationProcess();
        if (isInvitationProcess) {
            this.loginService.setInvitationProcess(false);
            this.loginService.navigateRegistration(this.authService.authModel);
        } else {
            this.loginService.setFormDataLoginResponse(response);
            this.loginService.navigate('/set-password');    
        }
    }

    verifyResetPasswordCode(code: string): Observable<ITokenInfo> {
        const payload: IUserPasswordResetVerificationPayload = {
            passwordResetToken: this.resetPasswordPayload.jwt,
            code: code,
        };

        return this.pnpUserService.putUserPasswordResetVerification(payload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    if(this.activeError === AuthErrors.TokenExpired || this.activeError === AuthErrors.TokenNotValid){
                        this.loginService.navigate('/login');
                    }        
                    return of(EMPTY);
                }),
                map((response: any) => {
                    return response;
                }),
            );
    }

    async onResendResetPasswordClicked($event: any) {
        this.activeError = null;
        const payload: IUserPasswordResetResendPayload = {
            passwordResetToken: this.resetPasswordPayload.jwt,
        };

        const resendToken = await firstValueFrom(await this.resendResetPasswordCode(payload));
        
        if(this.activeError) {
            return;
        }

        this.resetPasswordPayload.jwt = resendToken.jwt; 
    }

    resendResetPasswordCode(payload: IUserPasswordResetResendPayload) {
        return this.pnpUserService.putUserPasswordResetResend(payload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response: any) => {
                    return response;
                }),
            );
    }
}
